import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from 'components/UnvUI'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import styles from './StudentLifePage.module.scss'

const fmtDate = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('ru-RU')
  } catch {
    return '—'
  }
}

const StudentLifePage = () => {
  const { t } = useTranslation('common')
  const TABS = [
    { key: 'sport', label: t('studentLife.tab_sport') },
    { key: 'creative', label: t('studentLife.tab_creative') },
    { key: 'discipline', label: t('studentLife.tab_discipline') },
    { key: 'awards', label: t('studentLife.tab_awards') },
    { key: 'council', label: t('studentLife.tab_council') },
  ]
  const [tab, setTab] = useState('sport')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState({ student: '', title: '', date: '', notes: '' })
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get(`/student-life/${tab}/`)
      setItems(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [tab])

  const submit = async () => {
    try {
      await $api.post(`/student-life/${tab}/`, form)
      setNotify({ isOpen: true, message: t('studentLife.record_added'), type: 'success', sound: 'success' })
      setAddOpen(false)
      setForm({ student: '', title: '', date: '', notes: '' })
      load()
    } catch {
      setNotify({ isOpen: true, message: t('common.error'), type: 'error', sound: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <h1 className={styles.title}>{t('studentLife.title_page')}</h1>
        <Button variant="contained" onClick={() => setAddOpen(true)}>
          {t('studentLife.add_record_btn')}
        </Button>
      </div>

      <div className={`unv-card ${styles.tabsWrap}`}>
        <div className={styles.tabs}>
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`unv-btn ${tab === t.key ? '' : 'unv-btn--ghost'}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`unv-card ${styles.content}`}>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        ) : error ? (
          <div className="unv-empty">{t('studentLife.section_in_dev')}</div>
        ) : items.length === 0 ? (
          <div className="unv-empty">{t('studentLife.no_records')}</div>
        ) : (
          <table className="unv-table">
            <thead>
              <tr>
                <th>{t('studentLife.col_student')}</th>
                <th>{t('studentLife.col_event')}</th>
                <th>{t('studentLife.col_date')}</th>
                <th>{t('studentLife.col_notes')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.student_name || it.student?.full_name || '—'}</td>
                  <td>{it.title || '—'}</td>
                  <td>{fmtDate(it.date || it.created_at)}</td>
                  <td>{it.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('studentLife.new_record_for')} — {TABS.find((tt) => tt.key === tab)?.label}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label={t('studentLife.field_student_id_or_name')}
            value={form.student}
            onChange={(e) => setForm((f) => ({ ...f, student: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('studentLife.field_event')}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            margin="normal"
          />
          <TextField
            type="date"
            fullWidth
            label={t('studentLife.field_date')}
            InputLabelProps={{ shrink: true }}
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('studentLife.field_notes')}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submit}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default StudentLifePage
